import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Grid, Link,
  IconButton, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Tooltip,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { fetchBusinessDomain, deleteBusinessDomain, updateBusinessDomain } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";
import KpiConstraintAlert from "../../components/common/KpiConstraintAlert";
import KpiDetailDialog from "../../components/common/KpiDetailDialog";
import { useKpiUsageStatus } from "../../hooks/useKpiUsageStatus";

export default function BusinessDomainDetailPage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [strategyMapUrl, setStrategyMapUrl] = useState("");
  const [strategyMapMiddleUrl, setStrategyMapMiddleUrl] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // 事業指標
  const emptyIndicator = { indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" };
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditIndex, setKpiEditIndex] = useState(null); // null=新規, number=編集
  const [kpiForm, setKpiForm] = useState(emptyIndicator);
  const [kpiDeleteIndex, setKpiDeleteIndex] = useState(null);
  const [kpiDetailOpen, setKpiDetailOpen] = useState(false);
  const [kpiDetailRow, setKpiDetailRow] = useState(null);

  // KPI使用状況（成功要因に設定されているか）
  const { usedKpiNames } = useKpiUsageStatus(businessDomainId);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const loadData = async () => {
    try {
      const result = await fetchBusinessDomain(businessDomainId);
      setData(result);
      if (Array.isArray(result.associatedUrls)) {
        result.associatedUrls.forEach((item) => {
          if (item.urlTitle === "business_growth_model") {
            setStrategyMapUrl(item.url?.url || item.url || "");
          }
          if (item.urlTitle === "strategy_map_middle") {
            setStrategyMapMiddleUrl(item.url?.url || item.url || "");
          }
        });
      }
    } catch (e) {
      notify.apiError(e);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteBusinessDomain(businessDomainId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate("/design", { replace: true });
    } catch (e) {
      notify.apiError(e);
    }
  };

  // 事業指標: 追加・編集ダイアログを開く
  const openKpiDialog = (idx = null) => {
    if (idx !== null) {
      setKpiForm({ ...emptyIndicator, ...(data.indicators[idx] || {}) });
      setKpiEditIndex(idx);
    } else {
      setKpiForm(emptyIndicator);
      setKpiEditIndex(null);
    }
    setKpiDialogOpen(true);
  };

  // 事業指標: 保存（追加 or 更新）
  const handleKpiSave = async () => {
    try {
      const indicators = [...(data.indicators || [])];
      if (kpiEditIndex !== null) {
        indicators[kpiEditIndex] = kpiForm;
      } else {
        indicators.push(kpiForm);
      }
      const updated = await updateBusinessDomain({ ...data, indicators });
      setData(updated || { ...data, indicators });
      notify.success(kpiEditIndex !== null ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false);
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
  };

  // 事業指標: 削除
  const handleKpiDelete = async () => {
    try {
      const indicators = (data.indicators || []).filter((_, i) => i !== kpiDeleteIndex);
      await updateBusinessDomain({ ...data, indicators });
      notify.success(t("message.delete.success"));
      setKpiDeleteIndex(null);
      loadData();
    } catch (e) {
      notify.apiError(e);
    }
  };

  if (!data) return null;

  const indicators = data.indicators || [];
  const hasLockedKpi = indicators.some((ind) => usedKpiNames.has(ind.indicatorName));

  // KPI DataGrid 列定義
  const kpiColumns = [
    {
      field: "indicatorName",
      headerName: t("businessKpi.indicatorName"),
      flex: 1,
      minWidth: 150,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {p.row.indicatorName}
            {isLocked && (
              <Chip
                icon={<LockIcon fontSize="small" />}
                label={t("common.inUse")}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        );
      },
    },
    { field: "unit", headerName: t("businessKpi.unit"), width: 120 },
    { field: "description", headerName: t("common.description"), flex: 1, minWidth: 150 },
    {
      field: "actions",
      headerName: t("common.actions"),
      width: 140,
      sortable: false,
      renderCell: (p) => {
        const idx = indicators.findIndex((ind) => ind.indicatorName === p.row.indicatorName);
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={isLocked}
                  onClick={(e) => { e.stopPropagation(); openKpiDialog(idx); }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={isLocked}
                  onClick={(e) => { e.stopPropagation(); setKpiDeleteIndex(idx); }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      {/* KPI使用中アラート */}
      {hasLockedKpi && <KpiConstraintAlert variant="type-locked" />}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("businessDomain.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("businessDomain.name")} value={data.businessDomainName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("businessDomain.purpose")} value={data.purpose} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DetailField label={t("businessDomain.industryGroup")} value={data.industryGroupName || ""} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DetailField label={t("businessDomain.industry")} value={data.industryName || ""} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DetailField label={t("businessDomain.industrySubGroup")} value={data.industrySubGroupName || ""} />
          </Grid>
          <Grid size={12}>
            <DetailField
              label={t("businessDomain.strategyMapUrl")}
              value={
                strategyMapUrl ? (
                  <Link href={strategyMapUrl} target="_blank" rel="noopener noreferrer">
                    {strategyMapUrl}
                  </Link>
                ) : (
                  ""
                )
              }
            />
          </Grid>
          <Grid size={12}>
            <DetailField
              label={t("businessDomain.strategyMapMiddleUrl")}
              value={
                strategyMapMiddleUrl ? (
                  <Link href={strategyMapMiddleUrl} target="_blank" rel="noopener noreferrer">
                    {strategyMapMiddleUrl}
                  </Link>
                ) : (
                  ""
                )
              }
            />
          </Grid>
        </Grid>
      </DetailSection>

      {/* 事業指標セクション */}
      <Box sx={{ mt: 3, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t("businessKpi.list.title")}</Typography>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => openKpiDialog()}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={indicators}
          columns={kpiColumns}
          getRowId={(r) => r.indicatorName}
          autoHeight
          pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          onRowClick={(params) => { setKpiDetailRow(params.row); setKpiDetailOpen(true); }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      <Button variant="outlined" onClick={() => navigate(`/design/business-domains`)}>
        {t("common.back")}
      </Button>

      {/* KPI詳細ダイアログ（クリックで表示） */}
      <KpiDetailDialog
        open={kpiDetailOpen}
        onClose={() => { setKpiDetailOpen(false); setKpiDetailRow(null); }}
        indicator={kpiDetailRow}
      />

      {/* 事業指標 追加・編集ダイアログ */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditIndex !== null ? t("businessKpi.edit.title") : t("businessKpi.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField label={t("businessKpi.indicatorName")} required fullWidth value={kpiForm.indicatorName}
              onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            <TextField label={t("businessKpi.unit")} required fullWidth value={kpiForm.unit}
              onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
            <TextField label={t("businessKpi.measuringMethod")} fullWidth multiline rows={2} value={kpiForm.measuringMethod}
              onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
            <TextField label={t("businessKpi.evaluationStandard")} fullWidth multiline rows={2} value={kpiForm.evaluationStandard}
              onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
            <TextField label={t("common.description")} fullWidth multiline rows={2} value={kpiForm.description}
              onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave}
            disabled={!kpiForm.indicatorName.trim() || !kpiForm.unit.trim()}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 事業指標 削除確認 */}
      <ConfirmDialog
        open={kpiDeleteIndex !== null}
        onConfirm={handleKpiDelete}
        onCancel={() => setKpiDeleteIndex(null)}
      />

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
